import { PointCloudObject } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import { useEffect, useMemo, type JSX } from "react";
import { DataTexture, DoubleSide, NearestFilter, RGBAFormat } from "three";
import { floorColor } from "./floor-colors";
import { FloorSegmentation, IpxFloor } from "./floor-segmentation";
import { FloorBand, useFloorBandTint } from "./use-floor-band-tint";

/** Lift of the footprints above the floor points, so they don't z-fight */
const FOOTPRINT_LIFT = 0.02;

const OPAQUE = 255;

/** Tint strength of the floors: all of them, the one pointed at in the list, the others then */
const TINT = { normal: 0.6, highlighted: 0.85, dimmed: 0.2 };

/** Opacity of the footprints, as for the tint */
const FOOTPRINT_OPACITY = { normal: 0.25, highlighted: 0.45, dimmed: 0.08 };

/**
 * @param index Index of a floor
 * @param highlighted Index of the floor pointed at in the list, if any
 * @returns how much the floor stands out
 */
function emphasis(index: number, highlighted?: number): keyof typeof TINT {
  if (highlighted === undefined) return "normal";
  return highlighted === index ? "highlighted" : "dimmed";
}

type IpxFloorsHighlightProps = {
  /** The point cloud of the dataset */
  pointCloud: PointCloudObject;

  /** The floors to show */
  segmentation?: FloorSegmentation;

  /** False to hide the floors */
  visible: boolean;
};

/** @returns the detected floors: their points tinted, and the counted area as a translucent footprint */
export function IpxFloorsHighlight({
  pointCloud,
  segmentation,
  visible,
}: IpxFloorsHighlightProps): JSX.Element | null {
  const highlighted = useAppSelector(
    (state) => state.ipxOrderMode.highlightedFloorIndex,
  );
  const floors = useMemo(
    () => (visible ? (segmentation?.floors ?? []) : []),
    [segmentation, visible],
  );

  const bands = useMemo<FloorBand[]>(
    () =>
      floors.map((floor, index) => ({
        range: floor.band,
        color: floorColor(index),
        strength: TINT[emphasis(index, highlighted)],
      })),
    [floors, highlighted],
  );
  useFloorBandTint(pointCloud, bands);

  if (!segmentation || !floors.length) return null;
  const { position, rotation } = segmentation.frame;
  return (
    <>
      {floors.map((floor, index) => (
        <group
          key={index}
          position={[
            position[0],
            floor.elevation + FOOTPRINT_LIFT,
            position[2],
          ]}
          quaternion={rotation}
        >
          <FloorFootprint
            floor={floor}
            color={floorColor(index)}
            opacity={FOOTPRINT_OPACITY[emphasis(index, highlighted)]}
          />
        </group>
      ))}
    </>
  );
}

type FloorFootprintProps = {
  /** The floor to show */
  floor: IpxFloor;

  /** Color of the footprint */
  color: string;

  /** Opacity of the footprint */
  opacity: number;
};

/** @returns the counted cells of a floor, as a plane textured with the footprint mask */
function FloorFootprint({
  floor,
  color,
  opacity,
}: FloorFootprintProps): JSX.Element | null {
  const { cols, rows, cellSize, minX, minZ, mask } = floor.footprint;

  const texture = useMemo(() => {
    const data = new Uint8Array(cols * rows * 4);
    for (let row = 0; row < rows; row++) {
      // The plane is laid with its texture rows along -Z
      const texRow = rows - 1 - row;
      for (let col = 0; col < cols; col++) {
        const t = (texRow * cols + col) * 4;
        data.fill(OPAQUE, t, t + 3);
        data[t + 3] = mask[row * cols + col] ? OPAQUE : 0;
      }
    }
    const texture = new DataTexture(data, cols, rows, RGBAFormat);
    texture.magFilter = NearestFilter;
    texture.minFilter = NearestFilter;
    texture.needsUpdate = true;
    return texture;
  }, [cols, mask, rows]);
  useEffect(() => () => texture.dispose(), [texture]);

  if (!cols || !rows) return null;
  const width = cols * cellSize;
  const depth = rows * cellSize;
  return (
    <mesh
      position={[minX + width / 2, 0, minZ + depth / 2]}
      rotation-x={-Math.PI / 2}
      renderOrder={1}
    >
      <planeGeometry args={[width, depth]} />
      <meshBasicMaterial
        map={texture}
        color={color}
        transparent
        opacity={opacity}
        depthWrite={false}
        side={DoubleSide}
      />
    </mesh>
  );
}
